import { DollarSign, RefreshCw } from "lucide-react";

// We'll render a simplified version directly
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Upload,
  Trash2,
  FileSpreadsheet,
  Check,
  X,
  Download,
  Table,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table as UITable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { METHOD_BY_TABLE } from "@/components/quote/printMethods";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRICING_TABLES = [
  { key: "ultracolour_max", name: "UltraColour Max", description: "DTF pricing by quantity and size" },
  { key: "ultracolour_pro", name: "UltraColour Pro", description: "Hybrid printing with blocker options" },
  { key: "ultracolour_reflective", name: "UltraColour Reflective", description: "Sheet pricing by colour count (min 20 units, max 3 colours, $40 setup/colour)" },
  { key: "screen_print_pro_sheet", name: "Screen Print Pro Sheet", description: "Sheet pricing by quantity break, colour count and sheet size (Standard/Maxi/Mega)" },
  { key: "traditional_screen_print_matrix", name: "Traditional SP — Matrix", description: "Per-unit price by quantity range and colour count (leave MaxQty blank for 'and above')" },
  { key: "traditional_screen_print_charges", name: "Traditional SP — Charges", description: "One-off job charges. ChargeType is 'flat' or 'per-colour'" },
  { key: "screen_setup", name: "Screen Setup", description: "Setup cost per colour" },
  { key: "embroidery_tier", name: "Embroidery Tier", description: "Embroidery by quantity tier" },
  { key: "embroidery_setup", name: "Embroidery Setup", description: "One-time embroidery setup" },
  { key: "dtg", name: "DTG", description: "Direct-to-garment pricing" },
];

// Column templates so a CSV can be downloaded even when a table is empty.
const TEMPLATE_COLUMNS = {
  screen_print_pro_sheet: ["Qty", "Colours", "SheetSize", "CostPerSheet"],
  traditional_screen_print_matrix: ["MinQty", "MaxQty", "Colours", "PricePerUnit"],
  traditional_screen_print_charges: ["ChargeName", "ChargeType", "Amount"],
  ultracolour_reflective: ["MinSheets", "MaxSheets", "EffectiveColours", "IsWhiteOrBlack", "CostPerSheet"],
};

export default function PricingTablesContent() {
  const [tableData, setTableData] = useState({});
  const [tableCounts, setTableCounts] = useState({});
  const [activeTab, setActiveTab] = useState("ultracolour_max");
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [minOrderQty, setMinOrderQty] = useState(1);
  const [displayNames, setDisplayNames] = useState({});
  const [displayNameInput, setDisplayNameInput] = useState("");
  const [isSavingDisplayName, setIsSavingDisplayName] = useState(false);
  const fileInputRef = useRef(null);

  const getToken = () => localStorage.getItem("auth_token");

  useEffect(() => {
    loadTableCounts();
    loadTspConfig();
    loadDisplayNames();
  }, []);

  const loadDisplayNames = async () => {
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/settings/print-method-names`, { headers });
      setDisplayNames(res.data || {});
    } catch (error) {
      console.error("Failed to load print method display names", error);
    }
  };

  const saveDisplayName = async (methodKey) => {
    const value = (displayNameInput || "").trim();
    if (!value) {
      toast.error("Display name cannot be empty");
      return;
    }
    setIsSavingDisplayName(true);
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const updated = { ...displayNames, [methodKey]: value };
      await axios.put(`${API}/settings/print-method-names`, updated, { headers });
      setDisplayNames(updated);
      toast.success("Display name updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update display name");
    } finally {
      setIsSavingDisplayName(false);
    }
  };

  const loadTspConfig = async () => {
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/traditional-screen-print/config`, { headers });
      setMinOrderQty(res.data?.min_order_qty || 1);
    } catch (error) {
      console.error("Failed to load TSP config", error);
    }
  };

  const saveMinOrderQty = async () => {
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/traditional-screen-print/config`, { min_order_qty: minOrderQty }, { headers });
      toast.success("Minimum order quantity updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update minimum order quantity");
    }
  };

  useEffect(() => {
    if (activeTab) {
      loadTableData(activeTab);
      const methodDef = METHOD_BY_TABLE[activeTab];
      if (methodDef) {
        setDisplayNameInput(displayNames[methodDef.methodKey] ?? methodDef.defaultDisplayName);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, displayNames]);

  const loadTableCounts = async () => {
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/pricing`, { headers });
      setTableCounts(res.data || {});
    } catch (error) {
      console.error("Failed to load table counts", error);
    }
  };

  const loadTableData = async (tableKey) => {
    setIsLoading(true);
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/pricing/${tableKey}`, { headers });
      setTableData(prev => ({ ...prev, [tableKey]: res.data.data || [] }));
    } catch (error) {
      console.error(`Failed to load ${tableKey}`, error);
      setTableData(prev => ({ ...prev, [tableKey]: [] }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/pricing/${activeTab}/upload`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      toast.success("Pricing table uploaded successfully");
      loadTableData(activeTab);
      loadTableCounts();
    } catch (error) {
      console.error("Upload failed", error);
      toast.error(error.response?.data?.detail || "Failed to upload pricing table");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleClear = async () => {
    if (!confirm("Are you sure you want to clear this pricing table?")) return;
    
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/pricing/${activeTab}`, { headers });
      toast.success("Pricing table cleared");
      loadTableData(activeTab);
      loadTableCounts();
    } catch (error) {
      console.error("Clear failed", error);
      toast.error("Failed to clear pricing table");
    }
  };

  const currentTable = PRICING_TABLES.find(t => t.key === activeTab);
  const currentData = tableData[activeTab] || [];

  const handleDownload = () => {
    const cols = currentData.length > 0 ? Object.keys(currentData[0]) : (TEMPLATE_COLUMNS[activeTab] || []);
    if (cols.length === 0) {
      toast.error("No template available for this table");
      return;
    }
    const esc = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [cols.join(",")];
    currentData.forEach((row) => lines.push(cols.map((c) => esc(row[c])).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${activeTab}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const isTspTable = activeTab.startsWith("traditional_screen_print");

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            Pricing Tables
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage print method pricing, size tiers, and quantity breaks
          </p>
        </div>
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleUpload}
            className="hidden"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
            className="h-9 text-xs"
            data-testid="download-csv-btn"
          >
            <Download className="w-3 h-3 mr-1.5" />
            Download CSV Template
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleClear}
            disabled={currentData.length === 0}
            className="h-9 text-xs text-red-600 hover:text-red-700"
          >
            <Trash2 className="w-3 h-3 mr-1.5" />
            Clear
          </Button>
          <Button
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="h-9 text-xs"
          >
            <Upload className="w-3 h-3 mr-1.5" />
            {isUploading ? "Uploading..." : "Upload CSV"}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4 flex-wrap h-auto gap-1 bg-gray-100 p-1">
          {PRICING_TABLES.map((table) => (
            <TabsTrigger
              key={table.key}
              value={table.key}
              data-testid={`pricing-tab-${table.key}`}
              className="text-xs px-3 py-1.5 data-[state=active]:bg-white"
            >
              {table.name}
              {tableCounts[table.key] > 0 && (
                <Badge variant="secondary" className="ml-1.5 text-[10px] px-1.5">
                  {tableCounts[table.key]}
                </Badge>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={activeTab} className="mt-0">
          {currentTable && (
            <div className="mb-3 text-xs text-gray-500">
              {currentTable.description}
            </div>
          )}

          {METHOD_BY_TABLE[activeTab] && (
            <div className="mb-4 flex flex-wrap items-center gap-3 p-3 bg-teal-50 border border-teal-100 rounded-lg" data-testid="display-name-bar">
              <Label className="text-xs font-medium text-teal-900 whitespace-nowrap">
                Customer-facing Display Name
              </Label>
              <Input
                value={displayNameInput}
                onChange={(e) => setDisplayNameInput(e.target.value)}
                placeholder={METHOD_BY_TABLE[activeTab].defaultDisplayName}
                className="h-8 w-56 text-xs"
                data-testid="display-name-input"
              />
              <Button
                size="sm"
                onClick={() => saveDisplayName(METHOD_BY_TABLE[activeTab].methodKey)}
                disabled={isSavingDisplayName}
                className="h-8 text-xs"
                data-testid="display-name-save"
              >
                Save
              </Button>
              <span className="text-[11px] text-teal-700">
                The label shown on quotes, PDFs and Xero invoices for this method.
              </span>
            </div>
          )}

          {isTspTable && (
            <div className="mb-4 flex items-center gap-3 p-3 bg-blue-50 border border-blue-100 rounded-lg" data-testid="tsp-config-bar">
              <Label className="text-xs font-medium text-blue-900 whitespace-nowrap">
                Minimum Order Quantity
              </Label>
              <Input
                type="number"
                min="1"
                value={minOrderQty}
                onChange={(e) => setMinOrderQty(Math.max(1, parseInt(e.target.value) || 1))}
                className="h-8 w-24 text-xs"
                data-testid="tsp-min-order-qty"
              />
              <Button size="sm" onClick={saveMinOrderQty} className="h-8 text-xs" data-testid="tsp-min-order-save">
                Save
              </Button>
              <span className="text-[11px] text-blue-700">
                Applies to Traditional Screen Print quotes (not part of the CSV).
              </span>
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-12 text-gray-400">
              <RefreshCw className="w-5 h-5 mx-auto mb-2 animate-spin" />
              Loading...
            </div>
          ) : currentData.length === 0 ? (
            <div className="text-center py-12 text-gray-400 border-2 border-dashed rounded-lg">
              <FileSpreadsheet className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No data in this table</p>
              <Button
                variant="link"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                className="mt-2 text-blue-600"
              >
                Upload a CSV file
              </Button>
            </div>
          ) : (
            <ScrollArea className="h-[400px] border rounded-lg">
              <UITable>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    {Object.keys(currentData[0] || {}).map((col) => (
                      <TableHead key={col} className="text-xs font-medium">
                        {col}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentData.slice(0, 100).map((row, idx) => (
                    <TableRow key={idx}>
                      {Object.values(row).map((val, colIdx) => (
                        <TableCell key={colIdx} className="text-xs font-mono">
                          {val === null || val === undefined ? "" : (typeof val === "number" ? val.toFixed(2) : String(val))}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </UITable>
              {currentData.length > 100 && (
                <div className="p-2 text-center text-xs text-gray-500 bg-gray-50 border-t">
                  Showing first 100 of {currentData.length} rows
                </div>
              )}
            </ScrollArea>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
